import { parseExpression, importExpression, ParsedExpression } from '@sapappgyver/appgyver-formulas/parser';
import { ExecutableExpression, ExportedExpression } from '@sapappgyver/appgyver-formulas/runtime';
import isEmpty from 'lodash/isEmpty';
import { weakMapMemoize } from '@reduxjs/toolkit';
import { ARRAY_SCHEMA, OBJECT_SCHEMA, STRING_UNFORMATTED_SCHEMA } from './schemas';
import { hasArbitrarySchema } from './typings';
import { ReferenceOption } from '../composer/references/types';

export type ReferenceSourceScope =
  | 'pageVars'
  | 'appVars'
  | 'systemVars'
  | 'sensorVars'
  | 'params'
  | 'data'
  | 'current'
  | 'outputs'
  | 'inputs'
  | 'theme'
  | 'self'
  | 'internalProps'
  | 'outputValues'
  | 'privateVars'
  | 'privateDataVars'
  | 'privateDataResources'
  | 'repeated'
  | 'repeatedInfo'
  | 'translations'
  | 'schemas'
  | 'query'
  | 'response'
  | 'source'
  | 'request'
  | 'environment';

export type ReferenceSource = {
  scope: ReferenceSourceScope;
  title: string;
  description: string;
  disableIfNone: Array<ReferenceSourceScope> | null;
  defaultProperties: {};
};

const referenceSourceMapping: Record<string, ReferenceSource> = {
  'page-variable': {
    scope: 'pageVars',
    title: 'Page variables',
    description: 'Use value of a page variable',
    disableIfNone: null,
    defaultProperties: {},
  },
  'app-variable': {
    scope: 'appVars',
    title: 'App variables',
    description: 'Use value of a global app variable',
    disableIfNone: null,
    defaultProperties: {},
  },
  'system-variable': {
    scope: 'systemVars',
    title: 'System variables',
    description: 'Use information of the device and the system',
    disableIfNone: null,
    defaultProperties: {},
  },
  'sensor-variable': {
    scope: 'sensorVars',
    title: 'Sensor variables',
    description: 'Latest readings from the device sensors',
    disableIfNone: null,
    defaultProperties: {},
  },
  'parameter': {
    scope: 'params',
    title: 'Page parameters',
    description: 'Use value of a page parameter',
    disableIfNone: null,
    defaultProperties: {},
  },
  'data': {
    scope: 'data',
    title: 'Data variables',
    description: 'Use value from a data variable',
    disableIfNone: null,
    defaultProperties: {},
  },
  'current': {
    scope: 'current',
    title: 'Currently repeated data item',
    description: 'The object from the repeated data related to this specific component',
    disableIfNone: ['current'],
    defaultProperties: {},
  },
  'node': {
    scope: 'outputs',
    title: 'Output of another node',
    description: 'Result value of another logic node',
    disableIfNone: ['inputs', 'outputs'],
    defaultProperties: {},
  },
  'input': {
    scope: 'inputs',
    title: 'Input for this flow function',
    description: 'An input value that was passed to this flow function context.',
    disableIfNone: ['inputs', 'outputs'],
    defaultProperties: {},
  },
  'theme': {
    scope: 'theme',
    title: 'Theme variables',
    description: 'Values for the theme of the app',
    disableIfNone: null,
    defaultProperties: {},
  },
  'self': {
    scope: 'self',
    title: 'Selected component properties',
    description: 'Values of the properties the currently selected component has',
    disableIfNone: ['self'],
    defaultProperties: {},
  },
  'internal-property': {
    scope: 'internalProps',
    title: 'Component internal properties',
    description: 'Internal properties of the current component',
    disableIfNone: ['internalProps', 'outputValues', 'privateVars'],
    defaultProperties: {},
  },
  'output-value': {
    scope: 'outputValues',
    title: 'Component output values',
    description: 'Output values of the current component',
    disableIfNone: ['internalProps', 'outputValues', 'privateVars'],
    defaultProperties: {},
  },
  'private-variable': {
    scope: 'privateVars',
    title: 'Component private variables',
    description: 'Private variables of the current component',
    disableIfNone: ['internalProps', 'outputValues', 'privateVars'],
    defaultProperties: {},
  },
  'private-data-variable': {
    scope: 'privateDataVars',
    title: 'Component private data variables',
    description: 'Private data variables of the current component',
    disableIfNone: ['internalProps', 'outputValues', 'privateVars'],
    defaultProperties: {},
  },
  'data-interface': {
    scope: 'privateDataResources',
    title: 'Component data adapters',
    description: 'Data adapters of the current component',
    disableIfNone: ['internalProps', 'outputValues', 'privateVars'],
    defaultProperties: {},
  },
  'repeated': {
    scope: 'repeated',
    title: 'Currently repeated data items',
    description: 'The object from the repeated data related to this specific component',
    disableIfNone: ['repeated'],
    defaultProperties: {},
  },
  'repeatedInfo': {
    scope: 'repeatedInfo',
    title: 'Repeat information',
    description: 'Information currently repeated items and their positions in the repeat lists',
    disableIfNone: ['repeatedInfo'],
    defaultProperties: {},
  },
  'translation': {
    scope: 'translations',
    title: 'Translation variables',
    description: 'Text values that depend on the currently active language.',
    disableIfNone: null,
    defaultProperties: {},
  },
  'schema': {
    scope: 'schemas',
    title: 'Variable & resource schemas',
    description: 'Schema structures for different variables and resources',
    disableIfNone: null,
    defaultProperties: {
      pageVars: {
        title: 'Page variable schemas',
        description: 'Use a schema of a page variable',
        type: 'object',
        properties: {},
      },
      appVars: {
        title: 'App variable schemas',
        description: 'Use a schema of a global app variable',
        type: 'object',
        properties: {},
      },
      data: {
        title: 'Data variable schemas',
        description: 'Use a schema of a data variable',
        type: 'object',
        properties: {},
      },
      resources: {
        title: 'Data entity schemas',
        description: 'Use a schema of a data entity record',
        type: 'object',
        properties: {},
      },
    },
  },

  /* REST data connector */
  'query': {
    scope: 'query',
    title: 'Data query',
    description: 'Data connector query that includes conditions, paging, authentication and other parameters',
    disableIfNone: ['query'],
    defaultProperties: {},
  },
  'response': {
    scope: 'response',
    title: 'API response',
    description: 'The response received from the API. Run a test to find out how it responds.',
    disableIfNone: ['response'],
    defaultProperties: {},
  },

  /* Data mapping */
  'source': {
    scope: 'source',
    title: 'Source object',
    description: 'Source object that is being mapped.',
    disableIfNone: ['source'],
    defaultProperties: {},
  },

  /* Backend related sources */
  'request': {
    scope: 'request',
    title: 'Request event',
    description: 'Information about the current request being processed',
    disableIfNone: ['request', 'environment'],
    defaultProperties: {},
  },
  'environment': {
    scope: 'environment',
    title: 'Environment variables',
    description: 'All the constant configuration variables available for this environment',
    disableIfNone: ['request', 'environment'],
    defaultProperties: {},
  },
};

export type ReferenceSourceKey = keyof typeof referenceSourceMapping;

const basicContextRootNamespaces = Object.values(referenceSourceMapping);

// TODO: provide a more specific schema per adapter
export function getDataAdapterExpressionSchemaProperties(adapterReferenceOption: Record<string, any>) {
  const conditions = {
    ...OBJECT_SCHEMA,
    title: 'Query conditions',
    description: 'Query Conditions set in the adapter configuration screen',
  };
  const identifierVariables = {
    ...OBJECT_SCHEMA,
    properties: { ...adapterReferenceOption.identifierVariables },
  };
  const identifierKeys = { type: 'array', items: { type: 'string' } };
  return hasArbitrarySchema(adapterReferenceOption.schema)
    ? {
        conditions,
        identifierVariables,
        mergedFields: {
          ...ARRAY_SCHEMA,
          items: {
            ...OBJECT_SCHEMA,
            properties: {
              propertyName: STRING_UNFORMATTED_SCHEMA,
              propertyPath: { ...ARRAY_SCHEMA, items: STRING_UNFORMATTED_SCHEMA },
              title: STRING_UNFORMATTED_SCHEMA,
              type: STRING_UNFORMATTED_SCHEMA,
            },
          },
        },
        identifierKeys,
      }
    : {
        mapping: {
          ...OBJECT_SCHEMA,
          properties: { conditions, identifierVariables, identifierKeys },
        },
      };
}

/**
 * Builds a context schema object that can be passed to expressions'
 * analyze function from the given array of references (as originated
 * from ReferenceUtils).
 */
export const getExpressionContextSchema = weakMapMemoize(
  (referenceOptions: ReferenceOption[], expandAliases = false, whitelist: Array<ReferenceSourceScope> = []) => {
    // Build the context schema for the expression
    const contextSchema: Record<string, any> = {};

    const whitelistedContextRootNamespaces = whitelist.length
      ? basicContextRootNamespaces.filter((ns) => whitelist.includes(ns.scope))
      : basicContextRootNamespaces;

    whitelistedContextRootNamespaces.forEach(({ scope, title, description, defaultProperties }) => {
      contextSchema[scope] = {
        title,
        description,
        type: 'object',
        properties: { ...defaultProperties },
      };
    });
    referenceOptions.forEach((reference) => {
      const { schema, alias, hidden, readable, source, key } = reference;
      if (hidden || !readable) {
        return;
      }
      const refTarget = referenceSourceMapping[source as ReferenceSourceKey];
      if (refTarget) {
        const { scope } = refTarget;
        const { properties } = contextSchema[scope];
        if (scope === 'privateDataResources') {
          Object.keys(properties).forEach((refKey: string) => {
            if (!key.startsWith('adapter:')) {
              // TODO: overriding expression suggestions here until we have a better solution
              properties[refKey].properties = getDataAdapterExpressionSchemaProperties(properties[refKey]);
            }
          });
        }
        if (source === 'node') {
          const [outputName, nodeId] = key.split('@@@');
          const contextKey = (!expandAliases && alias) || nodeId;
          // Ignore references to the nested properties, because the schema of the variable
          // itself defines also nested properties.
          if (outputName.indexOf('.') < 0) {
            const nodeSchema = properties[contextKey] || {
              title: `Outputs for node "${contextKey}"`,
              type: 'object',
              properties: {},
              aliasFor: nodeId,
            };
            nodeSchema.properties[outputName] = { title: key, ...schema };
            properties[contextKey] = nodeSchema;
          }
        } else if (source === 'schema') {
          const [varScope, varName, ...nestedPath] = key.split('.');
          if (!nestedPath.length && varName) {
            const varObjects = properties[varScope];
            if (varObjects) {
              properties[varScope] = {
                ...varObjects,
                properties: {
                  ...varObjects.properties,
                  [varName]: {
                    title: varName,
                    ...schema,
                  },
                },
              };
            }
          }
        } else if (typeof key === 'string' && key.indexOf('.') < 0) {
          // Ignore references to the nested properties, because the schema of the variable
          // itself defines also nested properties.
          const contextKey = (!expandAliases && alias) || key;
          properties[contextKey] =
            source === 'repeated'
              ? {
                  ...schema,
                  title: `Repeated data item for ${key}`,
                  description: `The value from the repeated data named "${key}" related to the specific component`,
                  aliasFor: key,
                }
              : {
                  title: contextKey,
                  ...schema,
                  aliasFor: key,
                };
          if (source === 'repeated' && key === 'current') {
            // Add a shortcut namespace for "current"
            contextSchema.current = {
              ...schema,
              title: 'Currently repeated data item',
              description: 'The value from the repeated data related to the specific component',
            };
          }
        }
      }
    });
    // Remove context objects that should not be available at all if empty
    basicContextRootNamespaces.forEach(({ scope, disableIfNone }) => {
      if (disableIfNone?.every((reqScope) => isEmpty(contextSchema[reqScope]?.properties))) {
        delete contextSchema[scope];
      }
    });
    return contextSchema;
  },
);

/**
 * Modifies the expression so that context references are renamed to their aliases.
 * E.g.
 *
 *    outputs["sadf84.asdf43"].result
 *
 * would become:
 *
 *    outputs["My Logic Node"].result
 */
export function applyExpressionAliases(
  expression: ParsedExpression,
  contextSchema: Record<string, any>,
): ParsedExpression {
  const renames: Record<string, any> = {};
  Object.keys(contextSchema).forEach((rootKey) => {
    const { properties, type } = contextSchema[rootKey];
    if (type === 'object' && properties) {
      Object.keys(properties).forEach((key) => {
        const contextRenames = renames[rootKey] || {};
        renames[rootKey] = contextRenames;
        const schema = properties[key];
        if (schema && schema.aliasFor != null) {
          contextRenames[schema.aliasFor] = key;
        }
      });
    }
  });
  return expression.rename(renames);
}

/**
 * Modifies the expression so that context references are renamed from their
 * aliases back to original references.
 * E.g.
 *
 *    outputs["My Logic Node"].result
 *
 * would become:
 *
 *    outputs["sadf84.asdf43"].result
 *
 * @param {object} contextSchema Object describing schemas for each context key
 */
export function revertExpressionAliases(
  expression: ParsedExpression,
  contextSchema: Record<string, any>,
): ParsedExpression {
  const renames: Record<string, any> = {};
  Object.keys(contextSchema).forEach((rootKey) => {
    const { properties, type } = contextSchema[rootKey];
    if (type === 'object' && properties) {
      Object.keys(properties).forEach((key) => {
        const contextRenames = renames[rootKey] || {};
        renames[rootKey] = contextRenames;
        const schema = properties[key];
        if (schema && schema.aliasFor != null) {
          contextRenames[key] = schema.aliasFor;
        }
      });
    }
  });
  return expression.rename(renames);
}

export function getComputedAccessorString(accessor: string) {
  return `[${JSON.stringify(accessor)}]`;
}

export function getAccessorString(accessor: string) {
  if (typeof accessor === 'string' && /^[a-z_$][a-z0-9_$]*$/i.test(accessor)) {
    return `.${accessor}`;
  }
  return getComputedAccessorString(accessor);
}

/**
 * Creates a formula string that equals to the given binding as much as possible.
 * Returns null if conversion could not be done.
 */
function convertBindingToFormula(binding: any): string | null {
  if (typeof binding === 'string') {
    return JSON.stringify(binding);
  }
  if (typeof binding === 'number' || typeof binding === 'boolean') {
    return String(binding);
  }
  if (binding == null || typeof binding !== 'object') {
    return null;
  }
  const { source, value, key = value } = binding;
  if (source === 'expression') {
    return (key && key.formula) || null;
  }
  if (source === 'str') {
    return JSON.stringify(key || '');
  }
  if (key === '' || key == null) {
    return '';
  }
  if (source === 'num' || source === 'bool') {
    // 0, 123, true, false, etc.
    return String(key);
  }
  if (source === 'array') {
    if (!Array.isArray(key)) {
      return '[]';
    }
    return `[${key.map((itemBinding) => convertBindingToFormula(itemBinding) || 'null').join(', ')}]`;
  }
  if (source === 'object') {
    if (key == null || typeof key !== 'object' || Array.isArray(key)) {
      return '{}';
    }
    return `{${Object.keys(key)
      .map(
        (prop) =>
          `${/^[a-z][a-z0-9]*$/i.test(prop) ? prop : JSON.stringify(prop)}: ${
            convertBindingToFormula(key[prop]) || 'null'
          }`,
      )
      .join(', ')}}`;
  }
  if (source === 'component') {
    // Not yet supported by formulas
    return '';
  }
  if (source === 'schema') {
    return key;
  }
  if (source === 'repeated-legacy' || source === 'repeated') {
    return `repeated.${key}`;
  }
  const refSource = referenceSourceMapping[source as keyof typeof referenceSourceMapping];
  if (!refSource) {
    return null;
  }
  const { scope } = refSource;
  if (scope === 'outputs') {
    const match = /^(.*)@@@(.*?)$/.exec(key);
    if (match) {
      const propertyPath = match[1].split('.');
      const nodeId = match[2];
      const accessors = [nodeId].concat(propertyPath);
      return scope + accessors.map((p) => getAccessorString(p)).join('');
    }
  } else if (scope === 'translations') {
    return scope + getAccessorString(key);
  } else {
    const propertyPath = (key as string).split('.');
    return scope + propertyPath.map((p) => getAccessorString(p)).join('');
  }
  return '';
}

/**
 * Converts a binding source and key to an exported expression that can
 * be used as a binding key with an `expression` source. The return value
 * is an exported expression object.
 */
export function convertBindingToExpression(binding: any): ExportedExpression {
  if (binding) {
    const { source, key } = binding;
    if (source === 'expression' && key != null && typeof key === 'object') {
      // Binding is already an expression, no need to re-parse
      return key;
    }
  }
  const formula = convertBindingToFormula(binding);
  return parseExpression(formula || '').export();
}

const parsedExpressionCache =
  typeof WeakMap !== 'undefined' ? new WeakMap<ExportedExpression, ParsedExpression>() : undefined;

export function importCachedExpression(expression: ExportedExpression) {
  let importedExpression = parsedExpressionCache && parsedExpressionCache.get(expression);
  if (importedExpression == null) {
    importedExpression = importExpression(expression);
    if (parsedExpressionCache) {
      parsedExpressionCache.set(expression, importedExpression);
    }
  }
  return importedExpression;
}

const expressionExecutableCache =
  typeof WeakMap !== 'undefined' ? new WeakMap<ExportedExpression, ExecutableExpression>() : undefined;

export function prepareCachedExecutable(expression: ExportedExpression) {
  let executable = expressionExecutableCache && expressionExecutableCache.get(expression);
  if (executable == null) {
    executable = importExpression(expression).toExecutable();
    if (expressionExecutableCache) {
      expressionExecutableCache.set(expression, executable);
    }
  }
  return executable;
}

const nonSimpleRootContexts: (string | number)[] = ['sensorVars', 'systemVars'];

/**
 * Used by analytics system to guess if the given binding is an expression and
 * has something else than just constant or simple context access, except sensor or system
 * variable usage.
 * @param {object} expression exported expression object
 */
export function isNonSimpleExpressionBinding(binding: any) {
  if (!binding || binding.source !== 'expression') {
    // Not an expression binding
    return false;
  }
  const expression = binding.key || binding.value;
  if (!expression || typeof expression !== 'object') {
    // Not a valid expression binding
    return false;
  }
  let structure;
  try {
    const parsed = importCachedExpression(expression);
    // eslint-disable-next-line prefer-destructuring
    structure = parsed.structure;
  } catch (error) {
    // Ignore parsing errors
    return false;
  }
  if (!structure) {
    return false;
  }
  if (structure.type === 'constant') {
    // Expression is just a constant
    return false;
  }
  if (structure.type === 'contextAccess') {
    // Expression is just a context access
    return structure.name != null && nonSimpleRootContexts.indexOf(structure.name) >= 0;
  }
  if (structure.type === 'memberAccess' && structure.reference) {
    // Expression is just a reference chain
    return structure.reference != null && nonSimpleRootContexts.indexOf(structure.reference[0]) >= 0;
  }
  // Expression is more complex
  return true;
}
